import { sessionFetch } from './session-fetch';
import type { HostingServerPage, HostingServerView } from '@tosho/contracts';

export type ServerDescription = Pick<
  HostingServerView,
  'label' | 'provider' | 'hostingProviderId' | 'operatingSystem' | 'pleskDeclared' | 'controlPanels'
>;
export type ServerCreation = ServerDescription &
  Pick<HostingServerView, 'ownerKind' | 'ownerClientId' | 'administration'>;
export class HostingServerRequestError extends Error {
  public constructor(public readonly code: string) {
    super('Action sur le registre indisponible.');
  }
}
async function request<T>(
  path: string,
  method = 'GET',
  csrfToken?: string,
  body?: unknown,
): Promise<T> {
  const response = await sessionFetch('/api/hosting-servers' + path, {
    method,
    credentials: 'same-origin',
    cache: 'no-store',
    headers: {
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...(csrfToken === undefined ? {} : { 'X-CSRF-Token': csrfToken }),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (!response.ok) {
    const data: unknown = await response.json().catch(() => null);
    const code =
      data && typeof data === 'object' && 'code' in data && typeof data.code === 'string'
        ? data.code
        : response.status === 401 || response.status === 403
          ? 'ACCESS_DENIED'
          : 'UNAVAILABLE';
    throw new HostingServerRequestError(code);
  }
  return response.json() as Promise<T>;
}
export const hostingServerApi = {
  list: (page: number, size: number): Promise<HostingServerPage> =>
    request(`?page=${page}&pageSize=${size}`),
  create: (body: ServerCreation, csrf: string): Promise<HostingServerView> =>
    request('', 'POST', csrf, {
      ...description(body),
      ownerKind: body.ownerKind,
      ownerClientId: body.ownerClientId,
      administration: body.administration,
    }),
  describe: (
    server: HostingServerView,
    body: ServerDescription,
    csrf: string,
  ): Promise<HostingServerView> =>
    request('/' + encodeURIComponent(server.id), 'PATCH', csrf, {
      ...description(body),
      expectedRevision: server.revision,
    }),
};
// Une fiche lue contient plus de champs qu'une correction autorisée. Ne jamais
// propager une réponse complète ou un champ ajouté par un appelant au PATCH.
function description(body: ServerDescription): ServerDescription {
  return {
    label: body.label,
    provider: body.provider,
    ...(body.hostingProviderId === undefined ? {} : { hostingProviderId: body.hostingProviderId }),
    operatingSystem: body.operatingSystem,
    pleskDeclared: body.pleskDeclared,
    ...(body.controlPanels === undefined
      ? {}
      : {
          controlPanels: body.controlPanels.map((panel) => ({
            kind: panel.kind,
            present: panel.present,
            ...(panel.accessUrl === undefined ? {} : { accessUrl: panel.accessUrl }),
            ...(panel.accessComment === undefined ? {} : { accessComment: panel.accessComment }),
          })),
        }),
  };
}
export function hostingServerError(error: unknown): string {
  if (error instanceof HostingServerRequestError) {
    if (error.code === 'HOSTING_SERVER_CONFLICT')
      return 'Cette fiche a changé. Vos modifications ne sont pas enregistrées. Fermez et actualisez pour consulter sa dernière version.';
    if (error.code === 'HOSTING_SERVER_REQUEST_INVALID')
      return 'Vérifiez le nom du serveur et les informations saisies.';
    if (error.code === 'AUTHENTICATION_UNAVAILABLE')
      return 'La vérification de votre session est temporairement indisponible. Réessayez dans un instant.';
    if (error.code === 'ACCESS_DENIED')
      return 'Vous n’avez plus accès au registre. Reconnectez-vous ou contactez votre Administrateur.';
    if (error.code === 'HOSTING_SERVER_UNAVAILABLE')
      return 'Ce serveur ou le Client choisi n’est plus disponible. Actualisez avant de réessayer.';
  }
  return 'Le registre ne répond pas. Réessayez dans un instant.';
}
